import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { complaintsApi, apiUtils } from '../services/formsApi';
import { groupFieldsIntoSteps, EXTRA_STEP_TITLE } from '../utils/formSteps';
import ComplaintPortalHero from './ComplaintPortalHero';
import {
  AlertTriangle,
  Lock,
  User,
  FileText,
  Send,
  Info,
  Shield,
  Eye,
  EyeOff,
  Upload,
} from 'lucide-react';

const PublicComplaintFormV2 = ({ clientSlug }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  // Carregar formulário
  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        const response = await complaintsApi.getPublicForm(clientSlug);
        console.log('📋 Formulário carregado:', response);
        console.log('🏢 Company logo:', response.company_logo);
        console.log('🏢 Client name:', response.client_name);
        setForm(response);
        setFormData({});
        setErrors({});
      } catch (error) {
        console.error('Erro ao carregar formulário:', error);
        const errorInfo = apiUtils.handleError(error);
        toast.error(errorInfo.message);
      } finally {
        setLoading(false);
      }
    };

    if (clientSlug) {
      loadForm();
    }
  }, [clientSlug]);

  // Ícone de cada etapa. A REGRA de agrupamento vive em utils/formSteps, para
  // que o Form Management possa mostrar ao administrador exatamente onde cada
  // pergunta vai cair, sem risco de a prévia divergir daqui.
  const STEP_ICONS = {
    'Identificação': <User className="w-5 h-5" />,
    'Sobre Quem': <Shield className="w-5 h-5" />,
    'Detalhes da Denúncia': <AlertTriangle className="w-5 h-5" />,
    'Relato': <FileText className="w-5 h-5" />,
    [EXTRA_STEP_TITLE]: <Info className="w-5 h-5" />
  };

  const getFieldGroups = () => {
    if (!form?.fields) return [];
    return groupFieldsIntoSteps(form.fields).map((g) => ({
      ...g,
      icon: STEP_ICONS[g.title] || <Info className="w-5 h-5" />
    }));
  };

  const fieldGroups = getFieldGroups();
  const totalSteps = fieldGroups.length;

  const scrollToFormSection = useCallback(() => {
    document.getElementById('form-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  // Atualizar campo do formulário
  const updateField = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  // Verificar se campo deve ser exibido
  const shouldShowField = (field) => {
    if (!field.conditional) return true;
    
    const { field: conditionalField, value: expectedValue } = field.conditional;
    const actualValue = formData[conditionalField];
    
    const shouldShow = actualValue === expectedValue;
    
    // Debug para campos condicionais
    if (process.env.NODE_ENV !== 'production' && field.conditional) {
      console.log(`🔍 Campo condicional ${field.name}:`, {
        conditionalField,
        expectedValue,
        actualValue,
        shouldShow
      });
    }
    
    return shouldShow;
  };

  // Passo possui algum campo visível? (considera campos condicionais ocultos)
  const stepHasVisibleFields = (index) =>
    (fieldGroups[index]?.fields || []).some(shouldShowField);

  // Índice do último passo com conteúdo visível. Evita "passo fantasma" no fim
  // (ex.: grupo só com campos condicionais ocultos ou não-categorizados), fazendo
  // a última página real exibir "Enviar" em vez de "Próximo".
  const lastVisibleStepIndex = (() => {
    for (let i = totalSteps - 1; i >= 0; i--) {
      if (i === 0 || stepHasVisibleFields(i)) return i;
    }
    return 0;
  })();

  // Passos exibidos no indicador de progresso: apenas os com conteúdo visível
  // (esconde grupos "fantasma" — condicionais ocultos/não-categorizados).
  const visibleSteps = fieldGroups
    .map((group, index) => ({ group, index }))
    .filter(({ index }) => index === 0 || stepHasVisibleFields(index));

  // Verificar se deve mostrar campo "Outra opção" para campos com "Outra"/"Outros"
  const shouldShowOtherField = (field) => {
    if (!field.options || !Array.isArray(field.options)) return false;
    
    const hasOtherOption = field.options.some(option => {
      const optionText = typeof option === 'string' ? option : option.label || option.value;
      return optionText.toLowerCase().includes('outra') || optionText.toLowerCase().includes('outros');
    });
    
    if (!hasOtherOption) return false;
    
    const selectedValue = formData[field.name];
    if (!selectedValue) return false;
    
    // Verificar se a opção selecionada é "Outra" ou "Outros"
    const selectedOption = field.options.find(option => {
      const optionValue = typeof option === 'string' ? option : option.value;
      return optionValue === selectedValue;
    });
    
    if (!selectedOption) return false;
    
    const optionText = typeof selectedOption === 'string' ? selectedOption : selectedOption.label || selectedOption.value;
    return optionText.toLowerCase().includes('outra') || optionText.toLowerCase().includes('outros');
  };

  // Verificar disponibilidade do username
  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    
    setCheckingUsername(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app'}/api/complaints/check-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });
      
      const data = await response.json();
      setUsernameAvailable(data.available);
      
      if (!data.available) {
        setErrors(prev => ({ ...prev, anonymous_username: 'Username já está em uso' }));
      }
    } catch (error) {
      console.error('Erro ao verificar username:', error);
    } finally {
      setCheckingUsername(false);
    }
  };

  // Debounce para verificar username
  useEffect(() => {
    if (formData.identificacao === 'Não' && formData.anonymous_username) {
      const timer = setTimeout(() => {
        checkUsernameAvailability(formData.anonymous_username);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [formData.anonymous_username, formData.identificacao]);

  // Validar campo específico
  const validateField = (field) => {
    const value = formData[field.name];
    
    if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
      return `${field.label} é obrigatório`;
    }
    
    if (field.type === 'email' && value && !isValidEmail(value)) {
      return 'Email inválido';
    }
    
    // Validar campo "Outra opção" se necessário
    if (shouldShowOtherField(field)) {
      const otherValue = formData[`${field.name}_other`];
      if (!otherValue || otherValue.trim() === '') {
        return 'Especifique a opção personalizada';
      }
    }
    
    return null;
  };

  // Validar email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validar formulário completo
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;


    // Validar todos os campos visíveis
    form.fields?.forEach(field => {
      if (shouldShowField(field)) {
        const error = validateField(field);
        if (error) {
          newErrors[field.name] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Validar etapa atual
  const validateCurrentStep = () => {
    const currentFields = fieldGroups[currentStep]?.fields || [];
    const newErrors = {};
    let isValid = true;

    currentFields.forEach(field => {
      if (shouldShowField(field)) {
        const error = validateField(field);
        if (error) {
          newErrors[field.name] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Navegar entre etapas
  const goToNextStep = () => {
    // Se estiver na primeira etapa e for anônimo, verificar username
    if (currentStep === 0 && formData.identificacao === 'Não') {
      if (!formData.anonymous_username || formData.anonymous_username.length < 3) {
        toast.error('Por favor, escolha um username com pelo menos 3 caracteres');
        setErrors(prev => ({ ...prev, anonymous_username: 'Username deve ter pelo menos 3 caracteres' }));
        return;
      }
      
      if (usernameAvailable === false) {
        toast.error('Por favor, escolha um username disponível');
        setErrors(prev => ({ ...prev, anonymous_username: 'Username não disponível' }));
        return;
      }
      
      if (checkingUsername) {
        toast('Aguarde a verificação do username...', { icon: '⏳' });
        return;
      }
    }
    
    if (validateCurrentStep()) {
      setCurrentStep(prev => {
        let next = prev + 1;
        // Pular passos sem campos visíveis (condicionais ocultos/não-categorizados)
        while (next < lastVisibleStepIndex && !stepHasVisibleFields(next)) {
          next++;
        }
        return Math.min(next, lastVisibleStepIndex);
      });
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep(prev => {
      let p = prev - 1;
      while (p > 0 && !stepHasVisibleFields(p)) p--;
      return Math.max(p, 0);
    });
  };

  // Renderizar campo individual com novo design
  const renderField = (field) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    
    if (!shouldShowField(field)) return null;

    const baseInputClass = `w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 ${
      error 
        ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
        : 'border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-100'
    }`;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <div key={field.id} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type}
              value={value}
              onChange={(e) => updateField(field.name, e.target.value)}
              className={baseInputClass}
              placeholder={field.placeholder || `Digite ${field.label.toLowerCase()}`}
              required={field.required}
            />
            {error && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {error}
              </p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => updateField(field.name, e.target.value)}
              className={`${baseInputClass} min-h-[120px] resize-y`}
              placeholder={field.placeholder || `Descreva ${field.label.toLowerCase()}`}
              required={field.required}
            />
            {error && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {error}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-3">
              {field.options?.map((option, index) => {
                // Detectar se é objeto com value/label ou string simples
                const isObjectOption = typeof option === 'object' && option !== null;
                const optionValue = isObjectOption ? option.value : option;
                const optionLabel = isObjectOption ? option.label : option;
                
                return (
                <label key={index} className={`
                  flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all
                    ${value === optionValue 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}>
                  <input
                    type="radio"
                      value={optionValue}
                      checked={value === optionValue}
                    onChange={(e) => updateField(field.name, e.target.value)}
                    className="sr-only"
                    required={field.required}
                  />
                  <div className={`
                    w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center
                      ${value === optionValue ? 'border-blue-500' : 'border-gray-300'}
                  `}>
                      {value === optionValue && (
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                    )}
                  </div>
                    <span className="text-gray-700 font-medium">{optionLabel}</span>
                </label>
                );
              })}
            </div>
            
            {/* Campo "Outra opção" condicional */}
            {shouldShowOtherField(field) && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Especifique a opção:
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData[`${field.name}_other`] || ''}
                  onChange={(e) => updateField(`${field.name}_other`, e.target.value)}
                  className={baseInputClass}
                  placeholder="Digite sua opção personalizada"
                  required={true}
                />
                {errors[`${field.name}_other`] && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors[`${field.name}_other`]}
                  </p>
                )}
              </div>
            )}
            
            {error && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {error}
              </p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => updateField(field.name, e.target.value)}
              className={baseInputClass}
              required={field.required}
            >
              <option value="">Selecione uma opção</option>
              {field.options?.map((option, index) => {
                // Detectar se é objeto com value/label ou string simples
                const isObjectOption = typeof option === 'object' && option !== null;
                const optionValue = isObjectOption ? option.value : option;
                const optionLabel = isObjectOption ? option.label : option;
                
                return (
                  <option key={index} value={optionValue}>
                    {optionLabel}
                </option>
                );
              })}
            </select>
            
            {/* Campo "Outra opção" condicional */}
            {shouldShowOtherField(field) && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Especifique a opção:
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData[`${field.name}_other`] || ''}
                  onChange={(e) => updateField(`${field.name}_other`, e.target.value)}
                  className={baseInputClass}
                  placeholder="Digite sua opção personalizada"
                  required={true}
                />
                {errors[`${field.name}_other`] && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors[`${field.name}_other`]}
                  </p>
                )}
              </div>
            )}
            
            {error && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {error}
              </p>
            )}
          </div>
        );

      case 'file':
        return (
          <div key={field.id} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`
              relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200
              ${error 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
              }
            `}>
              <input 
                type="file" 
                onChange={(e) => updateField(field.name, e.target.files[0])}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xls,.xlsx"
              />
              <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              {value ? (
                <div>
                  <p className="text-sm font-medium text-gray-700">{value.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(value.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Clique para selecionar ou arraste um arquivo aqui
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, DOC, DOCX, JPG, PNG, GIF, TXT, XLS, XLSX (máx. 10MB)
                  </p>
                </>
              )}
            </div>
            {error && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {error}
              </p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-3">
              {field.options?.map((option, index) => {
                // Detectar se é objeto com value/label ou string simples
                const isObjectOption = typeof option === 'object' && option !== null;
                const optionValue = isObjectOption ? option.value : option;
                const optionLabel = isObjectOption ? option.label : option;
                
                return (
                <label key={index} className="flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all hover:bg-gray-50">
                  <input
                    type="checkbox"
                      value={optionValue}
                      checked={Array.isArray(value) ? value.includes(optionValue) : false}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      const newValues = e.target.checked
                          ? [...currentValues, optionValue]
                          : currentValues.filter(v => v !== optionValue);
                      updateField(field.name, newValues);
                    }}
                    className="mt-1 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
                  />
                    <span className="ml-3 text-gray-700">{optionLabel}</span>
                </label>
                );
              })}
            </div>
            {error && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {error}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Renderizar campos de login (tanto para anônimo quanto identificado)
  const renderLoginFields = () => {
    // Mostrar campos de login para AMBOS os casos
    if (!formData.identificacao || (formData.identificacao !== 'Sim' && formData.identificacao !== 'Não')) {
      return null;
    }

    const isAnonymous = formData.identificacao === 'Não';
    const bgColor = isAnonymous 
      ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200'
      : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200';
    const iconColor = isAnonymous ? 'text-yellow-600' : 'text-blue-600';

    return (
      <div className={`mb-8 p-6 ${bgColor} border-2 rounded-2xl`}>
        <div className="flex items-start mb-4">
          <Lock className={`w-6 h-6 ${iconColor} mr-3 mt-1`} />
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              {isAnonymous ? 'Credenciais Anônimas' : 'Credenciais de Acesso'}
            </h3>
            <p className="text-sm text-gray-600">
              {isAnonymous 
                ? 'Para acompanhar sua denúncia anonimamente, crie um username e senha.'
                : 'Crie um username e senha para acessar o sistema. Você também receberá as credenciais por email.'}
              {' '}Guarde estas informações com segurança!
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Username {isAnonymous ? 'Anônimo' : 'de Acesso'} *
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData[isAnonymous ? 'anonymous_username' : 'identified_username'] || ''}
                onChange={(e) => {
                  const fieldName = isAnonymous ? 'anonymous_username' : 'identified_username';
                  updateField(fieldName, e.target.value);
                  setUsernameAvailable(null);
                  // Limpar erro ao digitar
                  if (errors[fieldName]) {
                    setErrors(prev => ({ ...prev, [fieldName]: null }));
                  }
                }}
                className={`w-full px-4 py-3 pr-10 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 ${
                  errors.anonymous_username 
                    ? 'border-red-300 bg-red-50 focus:ring-red-100 focus:border-red-400' 
                    : usernameAvailable === false
                    ? 'border-red-300 bg-red-50 focus:ring-red-100 focus:border-red-400'
                    : usernameAvailable === true
                    ? 'border-green-300 bg-green-50 focus:ring-green-100 focus:border-green-400'
                    : 'border-gray-200 bg-white focus:ring-blue-100 focus:border-blue-400'
                }`}
                placeholder="Escolha um nome único (mín. 3 caracteres)"
              />
              {/* Indicador de status */}
              <div className="absolute right-3 top-3.5">
                {checkingUsername && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                )}
                {!checkingUsername && usernameAvailable === true && (
                  <div className="text-green-600">✓</div>
                )}
                {!checkingUsername && usernameAvailable === false && (
                  <div className="text-red-600">✗</div>
                )}
              </div>
            </div>
            {errors.anonymous_username && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {errors.anonymous_username}
              </p>
            )}
            {usernameAvailable === false && !errors.anonymous_username && (
              <p className="text-red-500 text-sm mt-2">
                Este username já está em uso. Por favor, escolha outro.
              </p>
            )}
            {usernameAvailable === true && (
              <p className="text-green-600 text-sm mt-2">
                Username disponível! ✓
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Senha {isAnonymous ? 'Anônima' : 'de Acesso'} *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData[isAnonymous ? 'anonymous_password' : 'identified_password'] || ''}
                onChange={(e) => updateField(isAnonymous ? 'anonymous_password' : 'identified_password', e.target.value)}
                className={`w-full px-4 py-3 pr-12 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 ${
                  errors.anonymous_password 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                    : 'border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-100'
                }`}
                placeholder="Mínimo 6 caracteres"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.anonymous_password && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {errors.anonymous_password}
              </p>
            )}
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-blue-50 rounded-xl">
          <p className="text-sm text-blue-800 flex items-start">
            <Info className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Importante:</strong> Guarde estas credenciais com segurança. 
              Você precisará delas para acessar e acompanhar sua denúncia.
            </span>
          </p>
        </div>
      </div>
    );
  };

  // Validar e enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevenir múltiplos cliques
    if (submitting) {
      console.log('⏳ Já está enviando, aguarde...');
      return;
    }
    
    // Log dos campos obrigatórios
    const requiredFields = form.fields?.filter(f => f.required) || [];
    console.log('📝 Campos obrigatórios:', requiredFields.map(f => ({
      name: f.name,
      label: f.label,
      value: formData[f.name],
      hasValue: !!formData[f.name]
    })));
    
    if (!validateForm()) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }
    
    // Validar credenciais (tanto anônimas quanto identificadas)
    if (formData.identificacao === 'Não' || formData.identificacao === 'Sim') {
      const newErrors = {};
      const isAnonymous = formData.identificacao === 'Não';
      const usernameField = isAnonymous ? 'anonymous_username' : 'identified_username';
      const passwordField = isAnonymous ? 'anonymous_password' : 'identified_password';
      
      if (!formData[usernameField]) {
        newErrors[usernameField] = `Username é obrigatório`;
      }
      
      if (!formData[passwordField] || formData[passwordField].length < 6) {
        newErrors[passwordField] = 'Senha deve ter pelo menos 6 caracteres';
      }
      
      // Para identificados, validar email também
      if (!isAnonymous && !formData.email) {
        newErrors.email = 'Email é obrigatório para denúncias identificadas';
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...newErrors }));
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      // Verificar se o formulário tem ID
      if (!form || !form.id) {
        console.error('❌ Formulário sem ID:', form);
        toast.error('Erro: formulário não carregado corretamente');
        setSubmitting(false);
        return;
      }
      
      // Preparar dados no formato esperado pela API
      const complaintData = {
        formId: form.id,
        isAnonymous: formData.identificacao === 'Não',
        formData: { ...formData }
      };
      
      // As credenciais viajam SEMPRE fora de formData, nos pacotes abaixo. Remover
      // os quatro campos incondicionalmente: antes só o par correspondente à
      // escolha era apagado, então quem preenchia um par e trocava de opção
      // deixava a senha para trás e ela era gravada em claro em `responses`.
      const CAMPOS_DE_CREDENCIAL = [
        'anonymous_username', 'anonymous_password',
        'identified_username', 'identified_password'
      ];

      // Adicionar credenciais baseado no tipo de identificação
      if (formData.identificacao === 'Não') {
        // Usuário anônimo
        complaintData.anonymousUser = {
          username: formData.anonymous_username,
          password: formData.anonymous_password
        };
      } else if (formData.identificacao === 'Sim') {
        // Usuário identificado - também precisa de credenciais
        complaintData.identifiedUser = {
          username: formData.identified_username,
          password: formData.identified_password,
          email: formData.email // Email já está no formData
        };
      }

      CAMPOS_DE_CREDENCIAL.forEach((campo) => delete complaintData.formData[campo]);

      // Debug: verificar dados antes de enviar
      console.log('🔍 Dados sendo enviados:', {
        formId: complaintData.formId,
        isAnonymous: complaintData.isAnonymous,
        formDataKeys: Object.keys(complaintData.formData),
        hasFile: complaintData.formData.arquivo instanceof File,
        anonymousUser: complaintData.anonymousUser ? 'presente' : 'ausente',
        anonymousUsername: complaintData.anonymousUser?.username || 'não definido',
        formDataComplete: complaintData.formData
      });
      
      // Log completo para debug
      console.log('📦 Objeto completo sendo enviado:', JSON.stringify(complaintData, null, 2));
      
      const response = await complaintsApi.createComplaint(complaintData);
      
      console.log('📨 Resposta da API:', response);
      
      if (response.ok || response.success) {
        toast.success('Denúncia enviada com sucesso!');
        
        // Redirecionar baseado no tipo de denúncia
        if (response.trackingCode) {
          // Se temos trackingCode, redirecionar para página de acompanhamento
          // ?novo=1 => a página de acompanhamento destaca o link e a confirmação de envio
          window.location.href = `/track/${response.trackingCode}?novo=1`;
        } else {
          // Fallback para página genérica
          window.location.href = '/denuncia-enviada';
        }
      } else {
        toast.error(response.message || 'Erro ao enviar denúncia');
      }
      
    } catch (error) {
      console.error('Erro ao enviar denúncia:', error);
      
      // Log detalhado do erro
      if (error?.response) {
        console.error('❌ Resposta do servidor:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        
        // Log específico do erro
        if (error.response.data?.error) {
          console.error('❌ Erro específico:', error.response.data.error);
        }
        if (error.response.data?.message) {
          console.error('❌ Mensagem:', error.response.data.message);
        }
      }
      
      if (error?.response?.data?.error && error.response.data.error.includes('Username já existe')) {
        toast.error('Nome de usuário anônimo já existe. Escolha outro nome para enviar sua denúncia.');
        setErrors(prev => ({ ...prev, anonymous_username: 'Username já existe' }));
        
        // Sugerir um username único
        const randomSuffix = Math.floor(Math.random() * 9999);
        const suggestedUsername = `${formData.anonymous_username || 'anonimo'}${randomSuffix}`;
        toast(`Sugestão de username: ${suggestedUsername}`, { 
          duration: 5000,
          icon: '💡'
        });
      } else if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error('Erro ao enviar denúncia. Por favor, tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          </div>
          <p className="text-gray-600 text-lg">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!form) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Formulário não encontrado</h2>
          <p className="text-gray-600">O formulário solicitado não está disponível.</p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#f3f5f9]">
      <ComplaintPortalHero
        clientSlug={clientSlug}
        form={form}
        onNavDenunciar={scrollToFormSection}
        onScrollToForm={scrollToFormSection}
      />

      {/* Seção do formulário */}
      <div id="form-section" className="max-w-4xl mx-auto px-4 py-8">
        {/* Header do formulário */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {form.title}
          </h1>
          {form.description && (
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {form.description}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {visibleSteps.map(({ group, index }, pos) => (
              <div
                key={index}
                className={`flex items-center ${
                  pos < visibleSteps.length - 1 ? 'flex-1' : ''
                }`}
              >
                <div
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full
                    ${index <= currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                    }
                  `}
                >
                  {group.icon}
                </div>
                {pos < visibleSteps.length - 1 && (
                  <div
                    className={`
                      flex-1 h-1 mx-2
                      ${index < currentStep ? 'bg-blue-600' : 'bg-gray-200'}
                    `}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm">
            {visibleSteps.map(({ group, index }) => (
              <div
                key={index}
                className={`
                  text-center
                  ${index <= currentStep ? 'text-blue-600 font-semibold' : 'text-gray-500'}
                `}
              >
                {group.title}
              </div>
            ))}
          </div>
        </div>

        {/* Form card */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8">
          {/* Current step fields */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              {fieldGroups[currentStep]?.icon}
              <span className="ml-3">{fieldGroups[currentStep]?.title}</span>
            </h2>
            

            {fieldGroups[currentStep]?.fields.map(field => renderField(field))}
            
            {/* Login fields if in identification step */}
            {currentStep === 0 && renderLoginFields()}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
              className={`
                px-6 py-3 rounded-xl font-semibold transition-all duration-200
                ${currentStep === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }
              `}
            >
              Anterior
            </button>

            {currentStep < lastVisibleStepIndex ? (
              <button
                type="button"
                onClick={goToNextStep}
                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center"
              >
                Próximo
                <Send className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className={`
                  px-8 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center
                  ${submitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  }
                `}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Denúncia
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Security info */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
          <div className="flex items-start">
            <Shield className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-gray-800 mb-2">Sua denúncia é confidencial</h3>
              <p className="text-sm text-gray-600">
                Todas as informações fornecidas serão tratadas com sigilo e segurança. 
                Nossa plataforma utiliza criptografia de ponta a ponta para proteger seus dados.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicComplaintFormV2;